import { useState } from 'react';
import { Typography, Input, Popover, Button, message } from 'antd';
import { EditOutlined, UserOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { setVisitorLabel } from '../../services/api';

const { Text } = Typography;

function LabelEditor({
  visitorHash,
  currentLabel,
  onLabelChange,
}: {
  visitorHash: string;
  currentLabel: string | null;
  onLabelChange?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(currentLabel || '');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (label: string) => setVisitorLabel(visitorHash, label),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analyticsVisitors'] });
      onLabelChange?.();
      message.success('Label updated');
      setOpen(false);
    },
    onError: () => {
      message.error('Failed to update label');
    },
  });

  const content = (
    <div style={{ width: '200px' }}>
      <Input
        placeholder="e.g. Mom, Jake, etc."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onPressEnter={() => mutation.mutate(value)}
        size="small"
        autoFocus
      />
      <div style={{ marginTop: '8px', display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
        {currentLabel && (
          <Button
            size="small"
            danger
            onClick={() => {
              setValue('');
              mutation.mutate('');
            }}
            loading={mutation.isPending}
          >
            Remove
          </Button>
        )}
        <Button
          size="small"
          type="primary"
          onClick={() => mutation.mutate(value)}
          loading={mutation.isPending}
          disabled={!value.trim() && !currentLabel}
        >
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      title="Name this visitor"
      trigger="click"
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) setValue(currentLabel || '');
      }}
    >
      <div
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          minWidth: '140px',
          maxWidth: '200px',
        }}
      >
        <UserOutlined style={{ color: currentLabel ? '#52c41a' : '#666', fontSize: '12px' }} />
        <Text
          style={{
            color: currentLabel ? '#f5f5f5' : '#888',
            fontSize: '13px',
            fontFamily: currentLabel ? 'inherit' : 'monospace',
          }}
          ellipsis
        >
          {currentLabel || visitorHash.slice(0, 10) + '...'}
        </Text>
        <EditOutlined style={{ color: '#666', fontSize: '11px', flexShrink: 0 }} />
      </div>
    </Popover>
  );
}

export default LabelEditor;
